import React, { useState } from "react";
import { getUsers } from "../services/api";
import { syncAllToBackend, getAudit, logAudit } from "../services/backend";

interface GdprItem { id: string; category: string; retention: string; lastReview: string; owner: string; notes: string; }
interface AccessReview { id: string; date: string; reviewer: string; usersReviewed: number; notes: string; }

const GDPR_KEY = "gdpr_register";
const ACCESS_KEY = "access_reviews";

const DEFAULT_GDPR: GdprItem[] = [
  { id: "g1", category: "Resident care records", retention: "8 years after departure/death", lastReview: "", owner: "Registered Manager", notes: "" },
  { id: "g2", category: "MAR & medication records", retention: "8 years", lastReview: "", owner: "Clinical Lead", notes: "" },
  { id: "g3", category: "Controlled drug register", retention: "2 years", lastReview: "", owner: "Clinical Lead", notes: "" },
  { id: "g4", category: "Safeguarding records", retention: "75 years", lastReview: "", owner: "Registered Manager", notes: "" },
  { id: "g5", category: "Staff records", retention: "6 years after leaving", lastReview: "", owner: "Home Manager", notes: "" },
  { id: "g6", category: "Invoices & financial records", retention: "6 years (HMRC)", lastReview: "", owner: "Administrator", notes: "" },
  { id: "g7", category: "Accident & incident records", retention: "10 years", lastReview: "", owner: "Deputy Manager", notes: "" },
];

const load = <T,>(key: string, fallback: T[]): T[] => {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw) as T[];
  } catch {}
  return fallback;
};
const saveKey = (key: string, data: any[]) => localStorage.setItem(key, JSON.stringify(data));
const loadArr = (key: string) => load(key, [] as any[]);
const daysTo = (d: string) => Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
const today = () => new Date().toISOString().slice(0, 10);

export default function Hardening({ user }: { user?: any }) {
  const [tab, setTab] = useState<"complete" | "gdpr" | "access" | "backup">("complete");
  const [gdpr, setGdpr] = useState<GdprItem[]>(() => load(GDPR_KEY, DEFAULT_GDPR));
  const [reviews, setReviews] = useState<AccessReview[]>(() => load(ACCESS_KEY, []));
  const [gdprForm, setGdprForm] = useState<Partial<GdprItem>>({});
  const [users, setUsers] = useState<any[]>([]);
  const [backup, setBackup] = useState<{ keys: number; lastAudit: string; synced: boolean } | null>(null);
  const [message, setMessage] = useState("");

  const say = (m: string) => { setMessage(m); setTimeout(() => setMessage(""), 4000); };

  const residents = loadArr("residents_records");
  const checksFor = (r: any) => {
    const intelligence = loadArr("intelligence_reports").filter((i: any) => i.residentName === r.name || i.resident === r.name);
    const wellbeing = loadArr("wellbeing_records").filter((w: any) => w.residentName === r.name);
    const meds = loadArr("meds_register").filter((m: any) => m.residentName === r.name);
    return [
      { label: "Funding & shares", ok: !!((r.fundingType || r.funder) && (parseFloat(r.residentPays) > 0 || parseFloat(r.councilPays) > 0 || r.funder === "Private" || r.fundingType === "Self-funding")) },
      { label: "Next of kin / legal rep", ok: !!(r.familyName || r.legalRep) },
      { label: "Assessment on file", ok: intelligence.length > 0 },
      { label: "Medication registered", ok: meds.length > 0 },
      { label: "Wellbeing review 60d", ok: wellbeing.some((w: any) => new Date(`${w.month}-01`).getTime() > Date.now() - 60 * 86400000) },
    ];
  };
  const scores = residents.map((r: any) => {
    const c = checksFor(r);
    return { name: r.name, score: Math.round((c.filter(x => x.ok).length / c.length) * 100), gaps: c.filter(x => !x.ok).map(x => x.label) };
  }).sort((a: any, b: any) => a.score - b.score);
  const homeAvg = scores.length ? Math.round(scores.reduce((s: number, x: any) => s + x.score, 0) / scores.length) : 0;

  const addGdpr = () => {
    if (!gdprForm.category) { say("Enter the record category."); return; }
    const next = [{ id: `g-${Date.now()}`, category: gdprForm.category || "", retention: gdprForm.retention || "", lastReview: gdprForm.lastReview || "", owner: gdprForm.owner || "Registered Manager", notes: gdprForm.notes || "" }, ...gdpr];
    setGdpr(next); saveKey(GDPR_KEY, next); setGdprForm({});
    say("Retention item added.");
  };

  const reviewGdpr = (id: string) => { const next = gdpr.map(g => (g.id === id ? { ...g, lastReview: today() } : g)); setGdpr(next); saveKey(GDPR_KEY, next); };

  const runAccessReview = async () => {
    try {
      const list = await getUsers();
      setUsers(Array.isArray(list) ? list : []);
      const rec: AccessReview = { id: `ar-${Date.now()}`, date: today(), reviewer: user && user.name ? user.name : "", usersReviewed: Array.isArray(list) ? list.length : 0, notes: "" };
      const next = [rec, ...reviews];
      setReviews(next); saveKey(ACCESS_KEY, next);
      logAudit(user, "ACCESS_REVIEW", "governance", "", `Access review completed: ${rec.usersReviewed} accounts checked`);
      say("Access review recorded - check the account list and deactivate leavers.");
    } catch {
      say("Could not load user accounts - check your connection.");
    }
  };

  const runBackupCheck = async () => {
    await syncAllToBackend(user);
    const a = await getAudit(1);
    const last = Array.isArray(a) && a[0] ? a[0].created_at : "none";
    setBackup({ keys: localStorage.length, lastAudit: last, synced: true });
    logAudit(user, "BACKUP_CHECK", "system", "", `Backup self-check: ${localStorage.length} local stores, last audit ${last}`);
    say("Backup self-check complete - data mirrored and audit trail verified.");
  };

  const lastReview = reviews[0];
  const accessOverdue = !lastReview || daysTo(lastReview.date) < -30;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Zero-Hole Hardening</h2>
        <p className="text-sm text-slate-600">Home-wide completeness, GDPR retention, access reviews and backup self-checks - the layer that makes 0 holes real.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {(["complete", "gdpr", "access", "backup"] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={tab === t ? "rounded-lg bg-blue-900 px-3 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-3 py-2 text-sm"}>{t === "complete" ? "Completeness" : t === "gdpr" ? "GDPR & Retention" : t === "access" ? "Access Reviews" : "Backup Check"}</button>
        ))}
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {tab === "complete" ? (
        <div className="space-y-4">
          <div className={`rounded-xl border-2 p-4 text-center ${homeAvg >= 90 ? "border-green-300 bg-green-50" : homeAvg >= 70 ? "border-amber-300 bg-amber-50" : "border-red-300 bg-red-50"}`}>
            <div className="text-3xl font-bold text-slate-900">{homeAvg}%</div>
            <div className="text-xs text-slate-600">Home-wide record completeness - target 100%</div>
          </div>
          <div className="space-y-2">
            {scores.map((s: any) => (
              <div key={s.name} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="text-sm font-medium text-slate-900">{s.name}</div>
                <div className="flex items-center gap-2">
                  {s.gaps.length ? <span className="text-xs text-red-700">Missing: {s.gaps.join(", ")}</span> : <span className="text-xs text-green-700">Complete</span>}
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${s.score === 100 ? "bg-green-100 text-green-700" : s.score >= 60 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{s.score}%</span>
                </div>
              </div>
            ))}
            {scores.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No residents yet.</div> : null}
          </div>
        </div>
      ) : null}

      {tab === "gdpr" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">Add a retention item</h3>
            <div className="grid gap-3 md:grid-cols-4">
              <input value={gdprForm.category || ""} onChange={e => setGdprForm(f => ({ ...f, category: e.target.value }))} placeholder="Record category" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input value={gdprForm.retention || ""} onChange={e => setGdprForm(f => ({ ...f, retention: e.target.value }))} placeholder="Retention period" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input value={gdprForm.owner || ""} onChange={e => setGdprForm(f => ({ ...f, owner: e.target.value }))} placeholder="Owner" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <button onClick={addGdpr} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Add</button>
            </div>
          </div>
          <div className="space-y-2">
            {gdpr.map(g => (
              <div key={g.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="text-sm text-slate-800">{g.category} <span className="text-xs text-slate-500">retain {g.retention} | owner {g.owner}{g.lastReview ? ` | reviewed ${g.lastReview}` : " | never reviewed"}</span></div>
                <button onClick={() => reviewGdpr(g.id)} className="rounded-lg border border-slate-300 px-2 py-1 text-xs">Mark reviewed today</button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {tab === "access" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={runAccessReview} className="rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white">Run monthly access review</button>
              {accessOverdue ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Access review overdue (monthly)</span> : <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">Last review {lastReview ? lastReview.date : "-"}</span>}
            </div>
            {users.length ? (
              <div className="mt-3 space-y-1">
                {users.map((u: any) => (
                  <div key={u.id} className="flex items-center justify-between rounded-lg border border-slate-100 p-2 text-sm">
                    <div>{u.name} <span className="text-xs text-slate-500">{u.email}</span></div>
                    <div className="text-xs">{u.role} | {u.active ? "active" : "inactive"}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div className="space-y-2">
            {reviews.map(r => (
              <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="text-sm text-slate-800">{r.date} | reviewed by {r.reviewer} | {r.usersReviewed} accounts checked</div>
              </div>
            ))}
            {reviews.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No access reviews recorded yet - run one monthly.</div> : null}
          </div>
        </div>
      ) : null}

      {tab === "backup" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <button onClick={runBackupCheck} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Run backup self-check now</button>
            <p className="mt-2 text-xs text-slate-500">Forces a sync of every local store to the MySQL backend and verifies the tamper-evident audit trail responded.</p>
          </div>
          {backup ? (
            <div className="rounded-xl border-2 border-green-300 bg-green-50 p-4">
              <div className="text-sm font-semibold text-green-800">Backup verified</div>
              <div className="mt-1 text-sm text-green-700">{backup.keys} local stores mirrored | last audit entry {backup.lastAudit}</div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}